import { Avatar } from "primereact/avatar";
import type { Button } from "primereact/button";
import { confirmPopup } from "primereact/confirmpopup";
import { Menu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";
import { useContext, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppointmentStore } from "src/features/appointment/services/useAppointmentStore";
import { useClinicsStore } from "src/features/clinics/services/useClinicsStore";
import { useFavouriteStore } from "src/features/favourite/services/useFavouriteStore";
import { useParticipantStore } from "src/features/participant/services/useParticipantStore";
import { ToastContext } from "../../../../../core/context/ToastContext";
import { logService } from "../../../../../core/services/logService";
import { useAuthStore } from "../../../../../features/auth/services/useAuthStore";
import "./LoggedInMenu.css";

export type LoggedInMenuProps = {
  loggedInMenuRef: RefObject<Menu | null>;
  loggedInMenuButtonRef: RefObject<Button | HTMLElement | null>;
};

export function LoggedInMenu({
  loggedInMenuRef,
  loggedInMenuButtonRef,
}: LoggedInMenuProps) {
  const toastRef = useContext(ToastContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const authStore = useAuthStore();
  const clinicsStore = useClinicsStore();
  const favouriteStore = useFavouriteStore();
  const participantStore = useParticipantStore();
  const appointmentStore = useAppointmentStore();

  const loggedInMenuItems: MenuItem[] = [
    {
      template: () => {
        return (
          <div className="flex align-items-center m-1">
            <Avatar
              label={authStore.user.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
              className="mr-2 profile-avatar"
            />

            <span className="inline-flex flex-column">
              <span className="font-bold">{authStore.user.name}</span>
              <span className="text-sm">{authStore.user.email}</span>
            </span>
          </div>
        );
      },
    },
    {
      separator: true,
    },
    {
      label: t("Home.Profile.Title"),
      icon: "pi pi-user-edit",
      command: () => {
        navigate("/profile");
      },
    },
    {
      label: t("Home.Logout.Title"),
      icon: "pi pi-sign-out",
      command: () => {
        confirmPopup({
          target: loggedInMenuButtonRef.current as HTMLElement,
          message: t("Home.Logout.Sure"),
          icon: "pi pi-question-circle",
          defaultFocus: "accept",
          accept: () => {
            toastRef?.current?.show({
              severity: "success",
              summary: t("Home.Logout.Title"),
              detail: t("Home.Logout.Bye", { value: authStore.user.name }),
            });

            authStore.logout();

            clinicsStore.clear();
            favouriteStore.clear();
            participantStore.clear();
            appointmentStore.clear();

            logService.log("User is logged out");

            navigate("/");
          },
        });
      },
    },
  ];

  return (
    <Menu
      className="loggedInMenu"
      popup
      model={loggedInMenuItems}
      ref={loggedInMenuRef}
    />
  );
}
